import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { Header } from "@/components/dashboard/Header";
import { TicketDrawer } from "@/components/dashboard/TicketDrawer";
import { Escalations } from "@/components/admin/Escalations";
import { Executives } from "@/components/admin/Executives";
import { WorkingHours } from "@/components/admin/WorkingHours";
import { Notifications } from "@/components/admin/Notifications";
import { Leaderboard } from "@/components/admin/Leaderboard";
import { ReplyTemplates } from "@/components/admin/ReplyTemplates";
import { Analytics } from "@/components/admin/Analytics";
import { Overview } from "@/components/admin/Overview";
import { Reviews } from "@/components/admin/Reviews";

export default function AdminPanel() {
  const [selected, setSelected] = useState(null);
  const { data: stats } = useQuery({ queryKey: ["stats"], queryFn: () => api.get("/tickets/stats").then((r) => r.data), refetchInterval: 60000 });
  const alarms = stats?.alarm_count ?? 0;

  const trigger = "rounded-xl border-2 border-transparent px-4 py-2 font-display text-sm font-bold data-[state=active]:border-black data-[state=active]:bg-black data-[state=active]:text-white sm:text-base";

  return (
    <div className="min-h-screen bg-[#FFFBEB] grain">
      <Header />
      <main className="mx-auto max-w-7xl space-y-6 px-4 py-6 sm:px-6">
        <div className={`rounded-2xl border-2 p-5 fade-up sm:p-6 ${alarms > 0 ? "border-[#DC2626] bg-[#DC2626] text-white" : "border-black bg-black text-white"}`} data-testid="admin-alarm-banner">
          <p className="text-xs font-bold uppercase tracking-[0.25em] opacity-80">Admin panel</p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
            {alarms > 0 ? `${alarms} ticket${alarms === 1 ? "" : "s"} need${alarms === 1 ? "s" : ""} attention now` : "No priority alarms. Queue is healthy."}
          </h1>
          <p className="mt-2 text-sm opacity-90">
            Priority alarm = unresolved beyond L3 threshold. {stats?.l2_count ? `${stats.l2_count} ticket(s) at L2 waiting.` : ""}
          </p>
        </div>

        <Tabs defaultValue="overview">
          <TabsList className="flex h-auto flex-wrap justify-start gap-1 rounded-xl border-2 border-black bg-white p-1" data-testid="admin-tabs">
            <TabsTrigger value="overview" className={trigger} data-testid="admin-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="escalations" className={trigger} data-testid="admin-tab-escalations">Escalations</TabsTrigger>
            <TabsTrigger value="executives" className={trigger} data-testid="admin-tab-executives">Team</TabsTrigger>
            <TabsTrigger value="hours" className={trigger} data-testid="admin-tab-hours">Working hours & SLA</TabsTrigger>
            <TabsTrigger value="leaderboard" className={trigger} data-testid="admin-tab-leaderboard">Leaderboard</TabsTrigger>
            <TabsTrigger value="analytics" className={trigger} data-testid="admin-tab-analytics">Analytics</TabsTrigger>
            <TabsTrigger value="reviews" className={trigger} data-testid="admin-tab-reviews">Reviews</TabsTrigger>
            <TabsTrigger value="templates" className={trigger} data-testid="admin-tab-templates">Saved replies</TabsTrigger>
            <TabsTrigger value="closing" className={trigger} data-testid="admin-tab-closing">Closing summaries</TabsTrigger>
            <TabsTrigger value="notifications" className={trigger} data-testid="admin-tab-notifications">Email & notifications</TabsTrigger>
          </TabsList>
          <TabsContent value="reviews" className="mt-5"><Reviews /></TabsContent>
          <TabsContent value="closing" className="mt-5"><ReplyTemplates kind="closing" /></TabsContent>
          <TabsContent value="overview" className="mt-5"><Overview onOpenTicket={setSelected} /></TabsContent>
          <TabsContent value="escalations" className="mt-5"><Escalations stats={stats} onOpen={setSelected} /></TabsContent>
          <TabsContent value="executives" className="mt-5"><Executives /></TabsContent>
          <TabsContent value="hours" className="mt-5"><WorkingHours /></TabsContent>
          <TabsContent value="leaderboard" className="mt-5"><Leaderboard /></TabsContent>
          <TabsContent value="analytics" className="mt-5"><Analytics /></TabsContent>
          <TabsContent value="templates" className="mt-5"><ReplyTemplates /></TabsContent>
          <TabsContent value="notifications" className="mt-5"><Notifications /></TabsContent>
        </Tabs>
      </main>
      <TicketDrawer ticket={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
